import React, { useEffect, useRef, useState } from "react";
import InviteUserModal from "../Modal/InviteUserModal";
import Spacing from "../Spacing";
import { Search, UserRoundPlus } from "lucide-react";
import UsersDataTable from "../DataTable/UsersDataTable";
import DataTablePagination from "../DataTablePagination";
import {
  deleteInvitation,
  fetchRoles,
  fetchUsersInvitations,
  inviteUser,
  inviteUserNew,
  updateInvitedUser,
} from "../../services/users";
import { notify, getPractice,  can_visit, userMessageTemplates } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { fetchAllUserProviders } from "../../services/users";

export default function Users({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [activeModal, setActiveModal] = useState(false);

  const [dataLoading, setDataLoading] = useState(true);
  const [messageTemplate] = useState(userMessageTemplates());
  const [usersList, setUsersList] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const isFetched = useRef(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [allProviders, setAllProviders] = useState([]);
  const getAllProviders = async () => {
    try {
      const res = await fetchAllUserProviders(account_id, location_id, 32, true);
      if (res?.data) {
        setAllProviders(res?.data);
      }
    } catch (err) { 
      // console.log("fetchAllUserProviders error", err);
    }

  };

  useEffect(() => {
    getAllProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    location_id: location_id,
    account_id: account_id,
    search_term: "",
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (!isFetched.current) {
      loadAllRoles();
      isFetched.current = true;
    }
  }, []);

  const loadAllRoles = async () => {
    try {
      const response = await fetchRoles();
      if (response?.data) {
        const roleNames = response?.data?.filter((role) => role.status === true).map((role) => role.role_name);
        setUserRoles(roleNames);
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      console.error("Error fetching loadAllRoles data:", error);
    }
  };

  const openActiveModal = () => {
    setActiveModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const closeActiveModal = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  const handleSubmit = async (data, callback = () => {}) => {
    if(can_visit("settings_users_invitations_create")){
      try {
        const res = await inviteUserNew({
          ...data,
          location_id: location_id || "",
          account_id,
          user_id,
        });
        if(res?.data?.status === "success"){
          await loadInvitations(filter);
          closeActiveModal();
          notify("success", res?.data?.message, 5);
        } else {
          notify("error", res?.data?.message, 5);
        }

      } catch (error) {
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data ||
          "Oops! Server throws error";
        notify("error", errorMessage, 5);
      }
    } else {
      closeActiveModal();
    }
    callback();
  };

  const handleResendInvitation = async (item, callback = () => {}) => {
    if(can_visit("settings_users_invitations_create")){
      try {
        await inviteUser(account_id, location_id, {
          ...item,
          resend: true,
          location_id: location_id || "",
          account_id,
        });
        await loadInvitations(filter);
        callback();
        notify("success", "Invitation sent to user successfully.", 2);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data ||
          "Oops! Server throws error";
        notify("error", errorMessage, 5);
        callback();
      }
      // setDataLoading(true);
    }
  };

  const loadInvitations = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchUsersInvitations(
        filter,
        account_id,
        location_id
      );
      const updatedData = response.data?.results?.map((row) => ({
        ...row,
        checked: false,
      }));
      setUsersList(updatedData);
      setPaginationCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        const errorMessage = error?.response?.data?.errorMessage || error?.response?.data?.detail || error?.response?.data?.message || error?.response?.data || "Oops! Server throws error";
        notify("error", errorMessage, 5);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeSearchTerm = (event) => {
    setFilter({ ...filter, search_term: event.target.value });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleCheckboxChange = (id) => {
    setUsersList((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  const handleSelectAll = () => {
    const newData = usersList.map((row) => ({
      ...row,
      checked: !selectAll,
    }));
    setUsersList(newData);
    setSelectAll(!selectAll);
  };

  const handleDeleteRecord = async (id) => {
    try {
      await deleteInvitation(id, account_id, location_id);
      await loadInvitations(filter);
      notify("success", "Record deleted successfully.", 0.5);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data ||
        "Oops! Server throws error";
      notify("error", errorMessage, 5);
    }
    // setDataLoading(true);
  };

  const handleUpdatedInvitedUserInfo = async (data, handleClose = () => {}) => {

    const filteredData = data?.roles?.filter((input) =>userRoles?.includes(input));
    data.roles = filteredData

    data.groups = data.groups.map((group) => {
      const parts = group.split("-");
      return parts.length > 1 ? parts[1].trim() : parts[0].trim();
    });

    if(can_visit("settings_users_invitations_create")){
      try {
       const res = await updateInvitedUser({
          ...data,
          account_id,
          location_id,
          user_id,
        });
        await loadInvitations(filter);
        if(res?.data?.status === "success"){
          notify("success", res?.data?.message, 5);
          handleClose();
        } else {
          notify("error", res?.data?.message, 5);
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data ||
          "Oops! Server throws error";
        notify("error", errorMessage, 5);
      }
      // setDataLoading(true);
    } else {
      notify("error", "You are not authorized to update this invitation", 5);
      handleClose();
    }
  };

  useEffect(() => {
    loadInvitations(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("Invitations");


  return (
    <>
      <div className="docpro_page_heading_wrap mb-0">
        <h2 className="docpro_page_heading docpro_fs_24">
          Invited Users
          <span>{usersList.length}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("settings") && can_visit("settings_users") && can_visit("settings_users_invitations_create")) && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
              onClick={openActiveModal}
            >
              <UserRoundPlus width={20} height={20} />
              <span>Invite new user</span>
            </button>
          )}
        </div>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>
          {/* <Filter filterType="advance" /> */}
        </div>
        <UsersDataTable
          allProviders={allProviders}
          tableData={usersList || []}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          handleDeleteRecord={handleDeleteRecord}
          handleResend={handleResendInvitation}
          handleUpdatedInvitedUserInfo={handleUpdatedInvitedUserInfo}
          messageTemplate={messageTemplate}
          reloadData={loadInvitations}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          userRoles={userRoles}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(usersList.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
      {activeModal && (
        <InviteUserModal          
          allProviders={allProviders}
          open={activeModal}
          closeDialog={closeActiveModal}
          handleSubmit={handleSubmit}
          userRoles={userRoles}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
